import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  CardMembership as CertificateIcon,
  FileDownload as FileDownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { studentAPI, certificateMasterAPI, studentCertificateAPI } from '../services/api';

const CertificateManagement = () => {
  const [students, setStudents] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [studentCertificates, setStudentCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState(null);
  const [openStudentCertDialog, setOpenStudentCertDialog] = useState(false);
  const [editingStudentCert, setEditingStudentCert] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isCertificateSectionExpanded, setIsCertificateSectionExpanded] = useState(false);
  
  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const studentsPerPage = 10;
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    issuing_organization: '',
  });

  const [studentCertFormData, setStudentCertFormData] = useState({
    student_id: '',
    certificate_id: '',
    acquired_date: '',
    score: '',
    memo: '',
  });

  // 학생 목록 조회
  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await studentAPI.getStudents();
      setStudents(response.data.students);
    } catch (error) {
      console.error('학생 목록 조회 실패:', error);
      showSnackbar('학생 목록 조회에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 자격증 목록 조회
  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const response = await certificateMasterAPI.getCertificateMasters();
      setCertificates(response.data.certificate_masters);
    } catch (error) {
      console.error('자격증 목록 조회 실패:', error);
      showSnackbar('자격증 목록 조회에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 학생별 자격증 취득 목록 조회
  const fetchStudentCertificates = async () => {
    try {
      const response = await studentCertificateAPI.getStudentCertificates();
      setStudentCertificates(response.data.student_certificates);
    } catch (error) {
      console.error('학생별 자격증 취득 목록 조회 실패:', error);
      showSnackbar('학생별 자격증 취득 목록 조회에 실패했습니다.', 'error');
    }
  };

  useEffect(() => {
    fetchStudents();
    fetchCertificates();
    fetchStudentCertificates();
  }, []);

  // 학생 데이터가 변경될 때 페이지를 1로 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [students]);

  // 폼 초기화
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      issuing_organization: '',
    });
    setEditingCertificate(null);
  };

  // 다이얼로그 열기
  const handleOpenDialog = (certificate = null) => {
    if (certificate) {
      setEditingCertificate(certificate);
      setFormData({
        name: certificate.name,
        description: certificate.description,
        issuing_organization: certificate.issuing_organization,
      });
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  // 다이얼로그 닫기
  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  // 폼 데이터 변경
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // 자격증 저장
  const handleSave = async () => {
    try {
      // 필수 필드 검증
      if (!formData.name || !formData.name.trim()) {
        showSnackbar('자격증명을 입력해주세요.', 'error');
        return;
      }
      
      if (!formData.issuing_organization || !formData.issuing_organization.trim()) {
        showSnackbar('발급기관을 입력해주세요.', 'error');
        return;
      }
      
      if (editingCertificate) {
        // 기존 자격증 수정
        await certificateMasterAPI.updateCertificateMaster(editingCertificate.id, formData);
        showSnackbar('자격증 정보가 수정되었습니다.', 'success');
      } else {
        // 새 자격증 추가
        await certificateMasterAPI.createCertificateMaster(formData);
        showSnackbar('자격증이 등록되었습니다.', 'success');
      }
      
      fetchCertificates(); // 목록 새로고침
      handleCloseDialog();
    } catch (error) {
      console.error('자격증 저장 실패:', error);
      showSnackbar('자격증 저장에 실패했습니다.', 'error');
    }
  };

  // 자격증 삭제
  const handleDelete = async (certificateId) => {
    if (window.confirm('정말로 이 자격증을 삭제하시겠습니까?')) {
      try {
        await certificateMasterAPI.deleteCertificateMaster(certificateId);
        showSnackbar('자격증이 삭제되었습니다.', 'success');
        fetchCertificates(); // 목록 새로고침
      } catch (error) {
        console.error('자격증 삭제 실패:', error);
        showSnackbar('자격증 삭제에 실패했습니다.', 'error');
      }
    }
  };

  // 학생별 자격증 취득 현황
  const getStudentCertificates = (studentId) => {
    return studentCertificates.filter(sc => sc.student_id === studentId);
  };

  // 페이지네이션 로직
  const activeStudents = students.filter(student => student.is_active);
  const totalPages = Math.ceil(activeStudents.length / studentsPerPage);
  const startIndex = (currentPage - 1) * studentsPerPage;
  const endIndex = startIndex + studentsPerPage;
  const currentStudents = activeStudents.slice(startIndex, endIndex);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  // 학생별 자격증 취득 다이얼로그 열기
  const handleOpenStudentCertDialog = (studentCert = null, studentId = null) => {
    if (studentCert) {
      setEditingStudentCert(studentCert);
      setStudentCertFormData({
        student_id: studentCert.student_id,
        certificate_id: studentCert.certificate_id,
        acquired_date: studentCert.acquired_date,
        score: studentCert.score || '',
        memo: studentCert.memo || '',
      });
    } else {
      setEditingStudentCert(null);
      setStudentCertFormData({
        student_id: studentId || '',
        certificate_id: '',
        acquired_date: '',
        score: '',
        memo: '',
      });
    }
    setOpenStudentCertDialog(true);
  };

  // 학생별 자격증 취득 다이얼로그 닫기
  const handleCloseStudentCertDialog = () => {
    setOpenStudentCertDialog(false);
    setEditingStudentCert(null);
    setStudentCertFormData({
      student_id: '',
      certificate_id: '',
      acquired_date: '',
      score: '',
      memo: '',
    });
  };

  // 학생별 자격증 취득 폼 데이터 변경
  const handleStudentCertInputChange = (field, value) => {
    setStudentCertFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // 학생별 자격증 취득 저장
  const handleStudentCertSave = async () => {
    try {
      // 필수 필드 검증
      if (!studentCertFormData.student_id) {
        showSnackbar('학생을 선택해주세요.', 'error');
        return;
      }
      
      if (!studentCertFormData.certificate_id) {
        showSnackbar('자격증을 선택해주세요.', 'error');
        return;
      }
      
      if (!studentCertFormData.acquired_date) {
        showSnackbar('취득일을 입력해주세요.', 'error');
        return;
      }
      
      if (editingStudentCert) {
        // 기존 자격증 취득 수정
        await studentCertificateAPI.updateStudentCertificate(editingStudentCert.id, studentCertFormData);
        showSnackbar('자격증 취득 정보가 수정되었습니다.', 'success');
      } else {
        // 새 자격증 취득 등록
        await studentCertificateAPI.createStudentCertificate(studentCertFormData);
        showSnackbar('자격증 취득이 등록되었습니다.', 'success');
      }
      
      fetchStudentCertificates(); // 목록 새로고침
      handleCloseStudentCertDialog();
    } catch (error) {
      console.error('자격증 취득 저장 실패:', error);
      showSnackbar('자격증 취득 저장에 실패했습니다.', 'error');
    }
  };

  // 학생별 자격증 취득 삭제
  const handleStudentCertDelete = async (studentCertId) => {
    if (window.confirm('정말로 이 자격증 취득 기록을 삭제하시겠습니까?')) {
      try {
        await studentCertificateAPI.deleteStudentCertificate(studentCertId);
        showSnackbar('자격증 취득 기록이 삭제되었습니다.', 'success');
        fetchStudentCertificates(); // 목록 새로고침
      } catch (error) {
        console.error('자격증 취득 삭제 실패:', error);
        showSnackbar('자격증 취득 삭제에 실패했습니다.', 'error');
      }
    }
  };

  // 스낵바 표시
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // 스낵바 닫기
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // 자격증 취득 현황 엑셀 내보내기
  const handleExportCertificatesToExcel = () => {
    try {
      // 활성 학생만 필터링
      const activeStudents = students.filter(student => student.is_active);
      
      // 엑셀 데이터 준비
      const excelData = [];
      
      activeStudents.forEach(student => {
        const studentCerts = getStudentCertificates(student.id);
        
        if (studentCerts.length > 0) {
          // 자격증이 있는 경우 각 자격증별로 행 생성
          studentCerts.forEach((cert, index) => {
            const certificateMaster = certificates.find(c => c.id === cert.certificate_id);
            excelData.push({
              '학생명': index === 0 ? student.name : '', // 첫 번째 행에만 학생명 표시
              '자격증명': certificateMaster?.name || '알 수 없음',
              '발급기관': certificateMaster?.issuing_organization || '-',
              '취득일': cert.acquired_date || '-',
              '점수': cert.score || '-',
              '메모': cert.memo || '-',
              '등록일': cert.created_at ? new Date(cert.created_at).toLocaleDateString('ko-KR') : '-'
            });
          });
        } else {
          // 자격증이 없는 경우 빈 행 생성
          excelData.push({
            '학생명': student.name,
            '자격증명': '-',
            '발급기관': '-',
            '취득일': '-',
            '점수': '-',
            '메모': '-',
            '등록일': '-'
          });
        }
      });

      // 워크시트 생성
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // 컬럼 너비 설정
      const columnWidths = [
        { wch: 15 }, // 학생명
        { wch: 20 }, // 자격증명
        { wch: 20 }, // 발급기관
        { wch: 12 }, // 취득일
        { wch: 10 }, // 점수
        { wch: 30 }, // 메모
        { wch: 12 }  // 등록일
      ];
      worksheet['!cols'] = columnWidths;

      // 워크북 생성
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '자격증 취득 현황');

      // 파일명 생성 (현재 날짜 포함)
      const now = new Date();
      const dateString = now.toISOString().split('T')[0];
      const fileName = `자격증_취득현황_${dateString}.xlsx`;

      // 파일 다운로드
      XLSX.writeFile(workbook, fileName);
      
      showSnackbar('자격증 취득 현황 엑셀 파일이 성공적으로 다운로드되었습니다.', 'success');
    } catch (error) {
      console.error('자격증 엑셀 내보내기 실패:', error);
      showSnackbar('자격증 엑셀 내보내기에 실패했습니다.', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <CertificateIcon sx={{ mr: 1 }} />
        <Typography variant="h4" component="h1">
          자격증 관리
        </Typography>
      </Box>

      {/* 자격증 종류 관리 */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" component="h2">
                자격증 종류 관리
              </Typography>
              <IconButton
                onClick={() => setIsCertificateSectionExpanded(!isCertificateSectionExpanded)}
                sx={{ ml: 1 }}
              >
                {isCertificateSectionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              자격증 등록
            </Button>
          </Box>
          
          {isCertificateSectionExpanded && (
            <Grid container spacing={2}>
              {certificates.map((certificate) => (
                <Grid item xs={12} sm={6} md={4} key={certificate.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6" component="div">
                          {certificate.name}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        발급기관: {certificate.issuing_organization}
                      </Typography>
                      {certificate.description && (
                        <Typography variant="body2" color="text.secondary">
                          설명: {certificate.description}
                        </Typography>
                      )}
                    </CardContent>
                    
                    <CardActions>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(certificate)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(certificate.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      {/* 학생별 자격증 취득 현황 */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h2">
              학생별 자격증 취득 현황
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={handleExportCertificatesToExcel}
                title="엑셀 내보내기"
              >
                <FileDownloadIcon />
              </IconButton>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenStudentCertDialog()}
              >
                자격증 취득 등록
              </Button>
            </Box>
          </Box>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>학생명</TableCell>
                  <TableCell>취득 자격증</TableCell>
                  <TableCell>취득일</TableCell>
                  <TableCell>점수</TableCell>
                  <TableCell align="center">작업</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentStudents.map((student) => {
                  const studentCerts = getStudentCertificates(student.id);
                  return (
                    <React.Fragment key={student.id}>
                      <TableRow>
                        <TableCell rowSpan={studentCerts.length || 1}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ mr: 1 }} />
                            {student.name}
                          </Box>
                        </TableCell>
                        {studentCerts.length > 0 ? (
                          <>
                            <TableCell>
                              {certificates.find(cert => cert.id === studentCerts[0].certificate_id)?.name || '알 수 없음'}
                            </TableCell>
                            <TableCell>{studentCerts[0].acquired_date}</TableCell>
                            <TableCell>{studentCerts[0].score || '-'}</TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenStudentCertDialog(studentCerts[0])}
                                  title="수정"
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleStudentCertDelete(studentCerts[0].id)}
                                  title="삭제"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell colSpan={4}>
                              <Typography variant="body2" color="text.secondary">
                                취득한 자격증이 없습니다.
                              </Typography>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                      {studentCerts.slice(1).map((cert, index) => (
                        <TableRow key={`${student.id}-${index}`}>
                          <TableCell>
                            {certificates.find(c => c.id === cert.certificate_id)?.name || '알 수 없음'}
                          </TableCell>
                          <TableCell>{cert.acquired_date}</TableCell>
                          <TableCell>{cert.score || '-'}</TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenStudentCertDialog(cert)}
                                title="수정"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleStudentCertDelete(cert.id)}
                                title="삭제"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 자격증 등록/수정 다이얼로그 */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCertificate ? '자격증 정보 수정' : '자격증 등록'}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="자격증명"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="발급기관"
                value={formData.issuing_organization}
                onChange={(e) => handleInputChange('issuing_organization', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="설명"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button onClick={handleSave} variant="contained">
            {editingCertificate ? '수정' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 학생별 자격증 취득 등록/수정 다이얼로그 */}
      <Dialog open={openStudentCertDialog} onClose={handleCloseStudentCertDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingStudentCert ? '자격증 취득 정보 수정' : '자격증 취득 등록'}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="학생"
                value={studentCertFormData.student_id}
                onChange={(e) => handleStudentCertInputChange('student_id', e.target.value)}
                SelectProps={{ native: true }}
                required
                disabled={!!editingStudentCert} // 수정 시에는 학생 변경 불가
              >
                <option value="">학생을 선택하세요</option>
                {students.filter(student => student.is_active).map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.name}
                  </option>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="자격증"
                value={studentCertFormData.certificate_id}
                onChange={(e) => handleStudentCertInputChange('certificate_id', e.target.value)}
                SelectProps={{ native: true }}
                required
              >
                <option value="">자격증을 선택하세요</option>
                {certificates.filter(cert => cert.is_active).map((certificate) => (
                  <option key={certificate.id} value={certificate.id}>
                    {certificate.name} ({certificate.issuing_organization})
                  </option>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="취득일"
                type="date"
                value={studentCertFormData.acquired_date}
                onChange={(e) => handleStudentCertInputChange('acquired_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="점수"
                value={studentCertFormData.score}
                onChange={(e) => handleStudentCertInputChange('score', e.target.value)}
                placeholder="점수를 입력하세요 (선택사항)"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="메모"
                multiline
                rows={3}
                value={studentCertFormData.memo}
                onChange={(e) => handleStudentCertInputChange('memo', e.target.value)}
                placeholder="메모를 입력하세요 (선택사항)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseStudentCertDialog}>취소</Button>
          <Button onClick={handleStudentCertSave} variant="contained">
            {editingStudentCert ? '수정' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CertificateManagement;
